import { useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface DeltaIndicatorProps {
  current: number;
  yoyPrior: number;
  popPrior: number;
  format?: 'percent' | 'absolute';
  invertColor?: boolean;
  popTooltip?: string;
  yoyTooltip?: string;
}

function DeltaLine({
  delta,
  label,
  tooltip,
  invertColor,
}: {
  delta: number;
  label: string;
  tooltip?: string;
  invertColor: boolean;
}) {
  const [showTip, setShowTip] = useState(false);
  const isPositive = delta >= 0;
  const isGood = invertColor ? !isPositive : isPositive;
  const colorClass = isGood ? 'text-success' : 'text-danger';
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const formatted = `${isPositive ? '+' : ''}${(delta * 100).toFixed(1)}%`;

  return (
    <span
      className={`relative inline-flex items-center gap-0.5 font-mono ${colorClass} text-2xs`}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <Icon className="w-3 h-3" />
      {formatted} <span className="text-text-3 ml-0.5">{label}</span>
      {showTip && tooltip && (
        <span className="absolute bottom-full left-0 mb-1 px-2 py-1 rounded bg-bg-2 border border-border text-text-3 text-[10px] whitespace-nowrap z-50 shadow-md pointer-events-none">
          {tooltip}
        </span>
      )}
    </span>
  );
}

export default function DeltaIndicator({
  current,
  yoyPrior,
  popPrior,
  invertColor = false,
  popTooltip,
  yoyTooltip,
}: DeltaIndicatorProps) {
  if (popPrior === 0 && yoyPrior === 0) return null;

  const popDelta = popPrior > 0 ? (current - popPrior) / popPrior : 0;
  const yoyDelta = yoyPrior > 0 ? (current - yoyPrior) / yoyPrior : 0;

  return (
    <div className="flex flex-col gap-0.5">
      {popPrior > 0 && (
        <DeltaLine delta={popDelta} label="PoP" tooltip={popTooltip} invertColor={invertColor} />
      )}
      {yoyPrior > 0 && (
        <DeltaLine delta={yoyDelta} label="YoY" tooltip={yoyTooltip} invertColor={invertColor} />
      )}
    </div>
  );
}
